import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { useSession } from "@clerk/clerk-react";
import { useMemo } from "react";
import type { Session } from "@clerk/clerk-react";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log("Supabase URL:", supabaseUrl);
console.log("Supabase Key exists:", !!supabaseAnonKey);

if (!supabaseUrl || !supabaseAnonKey) {
  console.error("Missing environment variables:");
  console.error("VITE_SUPABASE_URL:", supabaseUrl);
  console.error("VITE_SUPABASE_ANON_KEY exists:", !!supabaseAnonKey);
  throw new Error(
    "Missing Supabase environment variables. Please check your .env.local file.",
  );
}

// Default Supabase client (for unauthenticated requests)
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Debug utility to log JWT token information
 * Helps troubleshoot Clerk + Supabase integration issues
 */
async function debugClerkToken(session: Session | null) {
  if (!session) {
    console.log("🔍 Debug: No Clerk session available");
    return null;
  }

  try {
    const token = await session.getToken();
    if (token) {
      // Parse the JWT to see its contents (for debugging only)
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        console.log("🔍 Debug: Clerk JWT payload:", {
          sub: payload.sub,
          role: payload.role,
          iat: payload.iat,
          exp: payload.exp,
          iss: payload.iss,
        });
      }
    } else {
      console.log("🔍 Debug: Clerk session exists but no token returned");
    }
    return token;
  } catch (error) {
    console.error("🔍 Debug: Error getting/parsing Clerk token:", error);
    return null;
  }
}

/**
 * Creates a Supabase client that integrates with Clerk authentication.
 * This client automatically includes the Clerk session token in requests,
 * enabling Supabase RLS policies to work with Clerk user authentication.
 * 
 * Based on official Clerk + Supabase integration documentation.
 * https://supabase.com/docs/guides/auth/third-party/clerk
 * 
 * @param session - The Clerk session object from useSession() hook
 * @returns A Supabase client configured for Clerk authentication
 */
export function createClerkSupabaseClient(session: Session | null): SupabaseClient {
  return createClient(supabaseUrl, supabaseAnonKey, {
    global: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
    auth: {
      // Disable Supabase Auth since we're using Clerk
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    // Pass the Clerk session token for RLS authorization
    accessToken: async () => {
      try {
        if (!session) {
          console.log("🔍 No Clerk session - using anon access");
          return null;
        }

        const token = await session.getToken();
        
        // Debug logging (can be removed in production)
        if (import.meta.env.DEV) {
          await debugClerkToken(session);
        }

        return token || null;
      } catch (error) {
        console.error('❌ Error getting Clerk token:', error);
        return null;
      }
    },
  });
}

/**
 * Hook that creates a memoized Supabase client with Clerk authentication.
 * Use this in React components and hooks instead of createClerkSupabaseClient directly.
 * 
 * @returns A Supabase client that automatically handles Clerk JWT authentication
 */
export function useClerkSupabaseClient(): SupabaseClient {
  const { session } = useSession();
  
  return useMemo(() => {
    return createClerkSupabaseClient(session);
  }, [session]);
}

// Database Types
export interface Database {
  public: {
    Tables: {
      wgu_classes: {
        Row: {
          id: string;
          user_id: string;
          course_id: string;
          course_title: string;
          cus: number;
          status: "Not Started" | "In Progress" | "Completed" | "Transfer";
          actual_start_date: string | null;
          actual_end_date: string | null;
          planned_start_date: string | null;
          planned_end_date: string | null;
          term: string;
          planned_term: string;
          month_year_complete: string | null;
          wgu_subreddit_search: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          course_id: string;
          course_title: string;
          cus: number;
          status: "Not Started" | "In Progress" | "Completed" | "Transfer";
          actual_start_date?: string | null;
          actual_end_date?: string | null;
          planned_start_date?: string | null;
          planned_end_date?: string | null;
          term: string;
          planned_term: string;
          month_year_complete?: string | null;
          wgu_subreddit_search?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          course_id?: string;
          course_title?: string;
          cus?: number;
          status?: "Not Started" | "In Progress" | "Completed" | "Transfer";
          actual_start_date?: string | null;
          actual_end_date?: string | null;
          planned_start_date?: string | null;
          planned_end_date?: string | null;
          term?: string;
          planned_term?: string;
          month_year_complete?: string | null;
          wgu_subreddit_search?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
  };
}
